/**
 * DevicesTab — Lists devices assigned to the area with assign/remove controls.
 *
 * Columns: Device Name (link), Description, Model, Serial Number, Status
 * Actions: Assign Device button, per-row remove icon
 *
 * Remove is disabled for a device with an active alarm (alertedDeviceIds) as well
 * as for restricted device types the user cannot manage. AreaProfilePage repeats
 * both checks before the mutation, since an alarm can start while the confirm
 * dialog is open.
 *
 * Status uses the same dp-status pattern as the V2 Devices page:
 *   - Green (dp-status--active):   active, last_active within 30 hours
 *   - Yellow (dp-status--warning): active, last_active > 30 hours (stale) — not
 *     applied to manufacturers that never heartbeat (STALE_EXEMPT_MFG)
 *   - Grey (dp-status--inactive):  not active
 */

import StatusPopover from '../../../../../components/v2/StatusPopover.jsx';
import BlockedReasonTip from '../../../../../components/v2/BlockedReasonTip.jsx';
import { DEVICE_ALARM_BLOCKED_MSG } from '../../../../../utils/activeAlerts.js';
/* The shared device-link rule (B8). It also links wandering tags (6), which the
   local copy that was here sent to plain text. */
import { getDeviceProfileLink } from '../../../../../utils/deviceLinks.js';
import { Link } from 'react-router-dom';
import { skipsStaleCheck, showsMuted, MUTED_BADGE } from '../../../../../utils/deviceStatus.js';

const STALE_THRESHOLD_MS = 30 * 3600000; // 30 hours

/* Manufacturers on this surface whose devices never heartbeat: last_active never
   advances, so the stale test would permanently show them yellow. 10 is SIP /
   speakers, which report nothing until they are called. */
const STALE_EXEMPT_MFG = [10];

const DevicesTab = ({
  areaDevices,
  areaName,
  devEditRights,
  onAssign,
  onRemove,
  // Matches AreasPage's drawer: SIP (manufacture_id 10) and System (mid 1
  // + device_category_id 4/5) devices are restricted — only role weight
  // > 80 may add or remove them. Non-admins still see them in the list
  // for awareness but the remove button is disabled.
  canManageRestrictedDevices = true,
  isRestrictedDevice,
  /* Device ids with an active alarm anywhere on the campus. Removing one
     mid-alarm can stop its clear signal being processed, so the row's remove
     button is disabled while it is in here. */
  alertedDeviceIds,
}) => {
  const checkRestricted = (d) =>
    typeof isRestrictedDevice === 'function' ? isRestrictedDevice(d) : false;

  /** Derive status class and label from is_active + last_active */
  const getStatus = (device) => {
    if (!Number(device.is_active)) {
      return { className: 'dp-status--inactive', label: 'Inactive', title: undefined };
    }
    /* Manufacturers that never heartbeat can't be judged on last_active — see
       STALE_EXEMPT_MFG above. Skipping the test rather than faking a timestamp
       keeps the reason visible at the point the badge is chosen. */
    /* Suppression outranks staleness: it is a deliberate state someone set and can
       undo, while staleness is inferred from a timestamp. */
    if (showsMuted(device.is_active, device.away_until)) return MUTED_BADGE;
    const stale = !skipsStaleCheck(device.manufacture_id, STALE_EXEMPT_MFG)
      && (device.last_active
        ? (new Date() - new Date(device.last_active)) > STALE_THRESHOLD_MS
        : true);
    if (stale) {
      return {
        className: 'dp-status--warning',
        label: 'Active',
        title: 'Device may be stale — test the device to ensure functionality',
      };
    }
    return { className: 'dp-status--active', label: 'Active', title: undefined };
  };

  return (
    <div className="pp-section">
      <div className="pp-section__header">
        <h3 className="pp-section__title">
          Assigned Devices
          <span className="pp-section__count">({areaDevices?.length})</span>
        </h3>
        {devEditRights ? (
          <div className="pp-section__actions">
            <button className="dp-btn dp-btn--primary dp-btn--sm" onClick={onAssign}>
              + Assign Device
            </button>
          </div>
        ) : null}
      </div>
      <div className="pp-section__body pp-section__body--flush">
        <div className="dp-table-wrap">
          <table className="dp-table">
            <thead className="dp-thead">
              <tr>
                <th className="dp-th">Device Name</th>
                <th className="dp-th">Description</th>
                <th className="dp-th">Model</th>
                <th className="dp-th">Serial Number</th>
                <th className="dp-th dp-th--center">Status</th>
                {devEditRights ? <th className="dp-th dp-th--center" style={{ width: '80px' }}>Remove</th> : null}
              </tr>
            </thead>
            <tbody>
              {areaDevices.map((device) => {
                const status = getStatus(device);
                const deviceLink = getDeviceProfileLink(device);
                return (
                  <tr key={device.device_id} className="dp-tr">
                    <td className="dp-td">
                      {deviceLink ? (
                        <Link to={deviceLink} className="pp-field__value--link" style={{ fontSize: 'var(--font-size-sm)' }}>
                          {device.device_name || '—'}
                        </Link>
                      ) : (
                        <span className="dp-cell-text" style={{ fontSize: 'var(--font-size-sm)' }}>
                          {device.device_name || '—'}
                        </span>
                      )}
                    </td>
                    <td className="dp-td">
                      <span className="dp-cell-text">{device.descriptions || '—'}</span>
                    </td>
                    <td className="dp-td">
                      <span className="dp-cell-text">{device.model_name || device.manufacture_model || '—'}</span>
                    </td>
                    <td className="dp-td">
                      <span className="dp-cell-text dp-cell-text--mono">{device.manufacture_uuid || '—'}</span>
                    </td>
                    <td className="dp-td dp-td--center">
                      <StatusPopover message={status.title}>
                        <span className={`dp-status ${status.className}`}>
                          <span className="dp-status__dot" aria-hidden="true" />
                          {status.label}
                        </span>
                      </StatusPopover>
                    </td>
                    {devEditRights ? (
                      <td className="dp-td dp-td--center">
                        {(() => {
                          const restrictedBlocked = checkRestricted(device) && !canManageRestrictedDevices;
                          const alarmBlocked = Boolean(alertedDeviceIds?.has(String(device.device_id)));
                          const blocked = restrictedBlocked || alarmBlocked;
                          /* Alarm state first: it is the more surprising reason,
                             and it is temporary, so the copy says to wait rather
                             than implying the user lacks permission. */
                          const blockedReason = alarmBlocked
                            ? DEVICE_ALARM_BLOCKED_MSG
                            : (restrictedBlocked ? 'Only administrators may remove this device type.' : '');
                          /* BlockedReasonTip, not the button's own title: a disabled
                             control fires no pointer events, so a title on it would
                             never surface. The tip lives on a wrapper span (the CSS
                             takes the disabled button out of hit-testing so the hover
                             lands there), pins on tap for tablets, and renders
                             children bare when there is no message. */
                          return (
                            <BlockedReasonTip message={blockedReason}>
                              <button
                                className="pp-remove-btn"
                                onClick={() => onRemove(device)}
                                disabled={blocked}
                                title={
                                  blocked
                                    ? undefined
                                    : `Remove ${device.device_name || 'device'} from ${areaName}`
                                }
                              >
                                ✕
                              </button>
                            </BlockedReasonTip>
                          );
                        })()}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
              {areaDevices.length === 0 ? (
                <tr className="dp-tr">
                  <td className="dp-td dp-td--center" colSpan={devEditRights ? 6 : 5}>
                    <span className="dp-cell-text dp-cell-text--muted">No devices assigned to this area</span>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DevicesTab;
